import React, { useState, useMemo } from 'react';
import api from '../../api/api';
import { useNotifier } from '../../context/NotificationContext';
import DataTable from '../../components/common/DataTable';
import { createResourceService } from '../../services/api.service';
import Icon from '../../components/common/Icon';
import { icons } from '../../config/icons';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import AttendanceCalendar from '../../components/employee/AttendanceCalendar';

const attendanceService = createResourceService('employee-portal/attendance');

const MyAttendance = () => {
    const { notify } = useNotifier();
    const queryClient = useQueryClient();
    
    const [showModal, setShowModal] = useState(false);
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    const [formData, setFormData] = useState({ date: '', time: '', type: 'clock_in', reason: '', proof_image: null });
    const [leaveDataForm, setLeaveDataForm] = useState({ start_date: '', end_date: '', leave_type: 'Sick', reason: '' });

    const { data: attResponse } = useQuery({ queryKey: ['employee-portal/attendance'], queryFn: () => attendanceService.getAll() });
    const { data: leaveResponse } = useQuery({ queryKey: ['employee-portal/leaves'], queryFn: () => api.get('/api/employee-portal/leaves').then(res => res.data) });

    const attendanceData = attResponse?.data?.data || [];
    const leaveData = leaveResponse?.data || [];

    const handleRequestAdjustment = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const submitData = new FormData();
            submitData.append('date', formData.date);
            submitData.append('time', formData.time);
            submitData.append('type', formData.type);
            submitData.append('reason', formData.reason);
            
            // Construct local datetime and send as ISO to avoid server timezone offset issues
            const localDateTime = new Date(`${formData.date}T${formData.time}`);
            submitData.append('timestamp_iso', localDateTime.toISOString());
            
            if (formData.proof_image) {
                submitData.append('proof_image', formData.proof_image);
            }

            const res = await api.post('/api/employee-portal/attendance-request', submitData, { 
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                } 
            });
            if (res.data.success) {
                notify({ title: 'Success', message: 'Adjustment request submitted successfully.', style: 'success' });
                setShowModal(false);
                setFormData({ date: '', time: '', type: 'clock_in', reason: '', proof_image: null });
                queryClient.invalidateQueries({ queryKey: ['employee-portal/attendance'] });
            }
        } catch (error) {
            notify({ title: 'Error', message: error.response?.data?.message || 'Failed to submit request.', style: 'error' });
        }
    };

    const handleRequestLeave = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const res = await api.post('/api/employee-portal/leaves', leaveDataForm, { headers: { Authorization: `Bearer ${token}` } });
            if (res.data.success) {
                notify({ title: 'Success', message: 'Leave request submitted.', style: 'success' });
                setShowLeaveModal(false);
                queryClient.invalidateQueries({ queryKey: ['employee-portal/leaves'] });
            }
        } catch (error) {
            notify({ title: 'Error', message: error.response?.data?.message || 'Failed to request leave.', style: 'error' });
        }
    };

    const tableColumns = useMemo(() => [
        {
            key: 'log_date', label: 'Date', type: 1, sortable: true, render: (item) => (
                <div>{item.log_date ? new Date(item.log_date).toLocaleDateString() : '-'}</div>
            )
        },
        { key: 'clock_in', label: 'Clock In', type: 1, sortable: true, render: (item) => item.clock_in ? new Date(item.clock_in).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-' },
        { key: 'clock_out', label: 'Clock Out', type: 1, sortable: true, render: (item) => item.clock_out ? new Date(item.clock_out).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-' },
        { key: 'total_hours', label: 'Total Hours', type: 1, sortable: true, render: (item) => <span style={{ fontWeight: '600' }}>{item.total_hours || '-'}</span> },
        { key: 'status', label: 'Status', type: 1, sortable: true, render: (item) => {
            let badgeClass = 'light';
            if (item.status === 'Pending') badgeClass = 'warning';
            if (item.status === 'Approved') badgeClass = 'success';
            if (item.status === 'Rejected') badgeClass = 'error';
            return <span className={`td-badge ${badgeClass}`}>{item.status || 'Approved'}</span>;
        } },
        { key: 'remarks', label: 'Remarks', type: 1, sortable: false, render: (item) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {item.remarks && <span>{item.remarks}</span>}
                {item.client_remarks && <span style={{ color: 'var(--primary-color)', fontSize: '0.9em' }}>Admin: {item.client_remarks}</span>}
            </div>
        ) }
    ], []);

    const config = {
        title: (
            <span>
                <h2>My Attendance</h2>
                <p>View your timesheet and request manual adjustments.</p>
            </span>
        ),
        searchables: ['log_date', 'remarks'],
        add: false,
        edit: false,
        delete: false,
        archive: false,
        headerActions: [
            {
                text: 'View Calendar',
                icon: icons.calendar,
                type: 'button',
                className: 'button btn-primary',
                onClick: () => setShowCalendarModal(true)
            },
            {
                text: 'Request Leave',
                icon: icons.calendar,
                type: 'button',
                className: 'button btn-secondary',
                onClick: () => setShowLeaveModal(true)
            },
            {
                text: 'Request Adjustment',
                icon: icons.add,
                type: 'button',
                className: 'button btn-primary',
                onClick: () => setShowModal(true)
            }
        ]
    };

    return (
        <>
            <DataTable
                resourceName="employee-portal/attendance"
                apiService={attendanceService}
                columns={tableColumns}
                config={config}
            />

            {/* Calendar Modal */}
            {showCalendarModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
                    <div style={{ backgroundColor: 'var(--pagelet-bg)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '0', width: '100%', maxWidth: '1000px', color: 'var(--text-color)', maxHeight: '95vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 24px 24px 24px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Attendance Timeline</h3>
                            <button type="button" onClick={() => setShowCalendarModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '24px', lineHeight: '1' }}>
                                &times;
                            </button>
                        </div>
                        <div style={{ padding: '0 24px 24px 24px' }}>
                            <AttendanceCalendar 
                                attendanceData={attendanceData} 
                                leaveData={leaveData} 
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Request Adjustment Modal */}
            {showModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
                    <div style={{ backgroundColor: 'var(--pagelet-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '450px', color: 'var(--text-color)', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Request Time Adjustment</h3>
                            <button type="button" onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleRequestAdjustment} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)' }}>Date</label>
                                <input
                                    type="date"
                                    required
                                    value={formData.date}
                                    onChange={e => setFormData({ ...formData, date: e.target.value })}
                                    style={{ padding: '0 12px', height: '40px', borderRadius: '8px', border: '1px solid var(--border-color)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                                />
                            </div>
                            <div style={{ display: 'flex', gap: '16px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                                    <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)' }}>Type</label>
                                    <select
                                        value={formData.type}
                                        onChange={e => setFormData({ ...formData, type: e.target.value })}
                                        style={{ padding: '0 12px', height: '40px', borderRadius: '8px', border: '1px solid var(--border-color)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                                    >
                                        <option value="clock_in">Clock In</option>
                                        <option value="clock_out">Clock Out</option>
                                    </select>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                                    <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)' }}>Time</label>
                                    <input
                                        type="time"
                                        required
                                        value={formData.time}
                                        onChange={e => setFormData({ ...formData, time: e.target.value })}
                                        style={{ padding: '0 12px', height: '40px', borderRadius: '8px', border: '1px solid var(--border-color)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)' }}>Reason</label>
                                <textarea
                                    required
                                    rows="3"
                                    value={formData.reason}
                                    onChange={e => setFormData({ ...formData, reason: e.target.value })}
                                    placeholder="E.g., Forgot to clock in..."
                                    style={{ padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', minHeight: '80px', resize: 'vertical' }}
                                />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)' }}>Proof Image <span style={{ color: 'red' }}>*</span></label>
                                <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    required
                                    onChange={e => setFormData({ ...formData, proof_image: e.target.files[0] })}
                                    style={{ color: 'var(--text-color)', marginTop: '4px' }}
                                />
                            </div>
                            <hr style={{ border: 0, borderTop: '1px solid var(--border-color)', margin: '12px 0 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '10px' }}>
                                <button type="button" onClick={() => setShowModal(false)} className="button" style={{ backgroundColor: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-color)' }}>
                                    Cancel
                                </button>
                                <button type="submit" className="button btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Icon icon={icons.add} /> Submit Request
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Leave Request Modal */}
            {showLeaveModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
                    <div style={{ backgroundColor: 'var(--pagelet-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '450px', color: 'var(--text-color)', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Request Time Off</h3>
                            <button type="button" onClick={() => setShowLeaveModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleRequestLeave} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ display: 'flex', gap: '16px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                                    <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)' }}>Start Date</label>
                                    <input
                                        type="date"
                                        required
                                        value={leaveDataForm.start_date}
                                        onChange={e => setLeaveDataForm({ ...leaveDataForm, start_date: e.target.value })}
                                        style={{ padding: '0 12px', height: '40px', borderRadius: '8px', border: '1px solid var(--border-color)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                                    />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                                    <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)' }}>End Date</label>
                                    <input
                                        type="date"
                                        required
                                        value={leaveDataForm.end_date}
                                        onChange={e => setLeaveDataForm({ ...leaveDataForm, end_date: e.target.value })}
                                        style={{ padding: '0 12px', height: '40px', borderRadius: '8px', border: '1px solid var(--border-color)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)' }}>Leave Type</label>
                                <select
                                    value={leaveDataForm.leave_type}
                                    onChange={e => setLeaveDataForm({ ...leaveDataForm, leave_type: e.target.value })}
                                    style={{ padding: '0 12px', height: '40px', borderRadius: '8px', border: '1px solid var(--border-color)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                                >
                                    <option value="Sick">Sick Leave</option>
                                    <option value="Vacation">Vacation</option>
                                    <option value="Unpaid">Unpaid Leave</option>
                                </select>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)' }}>Reason</label>
                                <textarea
                                    required
                                    rows="3"
                                    value={leaveDataForm.reason}
                                    onChange={e => setLeaveDataForm({ ...leaveDataForm, reason: e.target.value })}
                                    placeholder="Please provide details..."
                                    style={{ padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', minHeight: '80px', resize: 'vertical' }}
                                />
                            </div>
                            <hr style={{ border: 0, borderTop: '1px solid var(--border-color)', margin: '12px 0 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '10px' }}>
                                <button type="button" onClick={() => setShowLeaveModal(false)} className="button" style={{ backgroundColor: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-color)' }}>
                                    Cancel
                                </button>
                                <button type="submit" className="button btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Icon icon={icons.calendar} /> Submit Leave Request
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default MyAttendance;
